import { useEffect, useRef, useState, type ReactNode } from 'react'

import { t } from '../../../i18n'
import {
  BOOST_RATE,
  LONG_PRESS_MS,
  nextBoostStep,
  seekTarget,
  zoneFor,
  type BoostStep,
} from '../longPress'
import { rateNumber } from '../playbackRate'
import { DIRECTION_LOCK_PX } from '../swipe'
import { BAND_HEIGHT } from './EpisodeBand'
import { DoubleDownIcon, DoubleUpIcon, FastForwardChase, LockIcon, SpeedIcon, UnlockIcon } from './icons'

interface Props {
  videoRef: React.RefObject<HTMLVideoElement | null>
  title: string
  episodeNumber: number
  /** 自动播放被拦后在静音播。这时底部要摆一句「点一下开声音」。 */
  muted: boolean
  /** 当前倍速的原始数值。长按两侧顶速时要记住它，松手未锁定时还原回它。 */
  rate: number
  /** 当前倍速，例如 1.0x。 */
  rateLabel: string
  /** 不是 1 倍时按钮点亮——人得一眼看出自己还挂着 2 倍，不然会以为片子本来就这么快。 */
  rateActive: boolean
  /** 当前的 2 倍速是不是长按上滑锁定来的。是的话再长按就提示「下滑退出」。 */
  rateLocked: boolean
  onOpenRate: () => void
  /** 长按视频中间、或点菜单图标，弹出播放菜单。 */
  onOpenQuickMenu: () => void
  /** 顶速时上滑后松手：锁定 2 倍速，跨集保持。 */
  onLockRate: () => void
  /** 锁定后再长按、下滑后松手：退出锁定，回到 1 倍。 */
  onUnlockRate: () => void
  /**
   * 长按两侧顶速期间（武装到松手）通知页面，为真时页面要临时关掉
   * EpisodeFeed 的竖滑换集判定——否则上滑锁定的位移会被当成切集的信号。
   */
  onBoostActiveChange: (active: boolean) => void
  /** 清屏播放：画面上的按钮和文字全部藏掉，只留一个「退出清屏」。 */
  clean: boolean
  onExitClean: () => void
  /** 临时提示（现在只有「网络慢，建议降档」）。放进底部这一摞里排，不要各自绝对定位。 */
  hint?: ReactNode
  onTap: () => void
  onPlayingChange: (playing: boolean) => void
  /** 选集面板开着：整层藏起来，也不接触摸（视频缩在上方照常播）。 */
  hidden: boolean
}

/** 锁定 / 退出之后那句结论在屏幕上停多久。和红果一样 3 秒。 */
const NOTICE_MS = 3000

interface Boost {
  step: BoostStep
  /** 顶速前的倍速，松手没锁定就还原回它。 */
  origin: number
  /** 长按触发那一刻的落点，所有位移都从这里算。 */
  anchorX: number
  anchorY: number
  /** 进入拖进度那一刻的手指横坐标和播放位置。 */
  seekFromX: number
  seekFromTime: number
  /** 拖到的位置。松手才真正 seek，拖的过程中只动进度条。 */
  seekTo: number
}

/**
 * 播放器的覆盖层：点按暂停、长按手势、顶部栏、底部剧名与进度条。和 App 的 PlayerOverlayView 同一套样子（照红果）；
 * 视频区下面的选集条不归这里管，在页面上（EpisodeBand）。
 *
 * **上下滑切集不在这里**——那是 EpisodeFeed 的事。
 * 两处都监听触摸的话，拖进度条时手指稍微上飘就会跳集。
 *
 * **长按手势收口也在这里**——和 tap 判定是同一组 handler，不另开监听器：
 * 中间长按弹菜单；两侧长按顶 2 倍速，按住期间上滑松手锁定、横拖调进度，
 * 锁定后再长按下滑松手退出。规则见 longPress.ts。
 *
 * 不用原生 controls：原生控件条会占掉一截画面、样式不可控，
 * 且在 Telegram 的 WebView 里各平台长得都不一样。
 */
export function PlayerControls({
  videoRef,
  title,
  episodeNumber,
  muted,
  rate,
  rateLabel,
  rateActive,
  rateLocked,
  onOpenRate,
  onOpenQuickMenu,
  onLockRate,
  onUnlockRate,
  onBoostActiveChange,
  clean,
  onExitClean,
  hint,
  onTap,
  onPlayingChange,
  hidden,
}: Props) {
  const [paused, setPaused] = useState(false)
  const [position, setPosition] = useState(0)
  const [duration, setDuration] = useState(0)
  const trackRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  /** 手指在进度条上拖。只驱动进度条变粗，判断仍读 dragging。 */
  const [scrubbing, setScrubbing] = useState(false)
  /** 这一次触摸有没有移动过。移动过就是在滑，不该顺手把片子停掉。 */
  const moved = useRef(false)

  /**
   * 长按判定全部用 ref，不用 state——松手那一刻必须读到最新值，
   * 不能等 touchmove 里的 setState 批到下一帧（决策记录 28）。
   */
  const pressTimer = useRef<number | null>(null)
  const pressStart = useRef({ x: 0, y: 0 })
  const lastPoint = useRef({ x: 0, y: 0 })
  const longPressFired = useRef(false)
  const boost = useRef<Boost | null>(null)
  /** 只驱动顶速角标和提示条，不参与任何手势判断——判断永远读上面那些 ref。 */
  const [boostStep, setBoostStep] = useState<BoostStep | null>(null)
  const [notice, setNotice] = useState<'locked' | 'restored' | null>(null)
  const noticeTimer = useRef<number | null>(null)

  function clearPressTimer() {
    if (pressTimer.current != null) {
      window.clearTimeout(pressTimer.current)
      pressTimer.current = null
    }
  }

  function showNotice(next: 'locked' | 'restored') {
    if (noticeTimer.current != null) window.clearTimeout(noticeTimer.current)
    setNotice(next)
    noticeTimer.current = window.setTimeout(() => setNotice(null), NOTICE_MS)
  }

  function startBoost() {
    const video = videoRef.current
    // 和红果一致：暂停中长按两侧不顶速
    if (!video || video.paused) return
    boost.current = {
      step: 'hold',
      origin: rate,
      anchorX: lastPoint.current.x,
      anchorY: lastPoint.current.y,
      seekFromX: 0,
      seekFromTime: 0,
      seekTo: 0,
    }
    video.playbackRate = BOOST_RATE
    setNotice(null)
    setBoostStep('hold')
    onBoostActiveChange(true)
  }

  function moveBoost(x: number, y: number) {
    const b = boost.current
    const video = videoRef.current
    if (!b || !video) return
    const step = nextBoostStep(b.step, x - b.anchorX, y - b.anchorY, rateLocked)
    if (step === 'seeking') {
      if (b.step !== 'seeking') {
        b.seekFromX = x
        b.seekFromTime = video.currentTime
        dragging.current = true
      }
      const width = trackRef.current?.getBoundingClientRect().width ?? 0
      b.seekTo = seekTarget(b.seekFromTime, x - b.seekFromX, width, video.duration || 0)
      setPosition(b.seekTo)
    }
    if (step !== b.step) {
      b.step = step
      setBoostStep(step)
    }
  }

  /**
   * 松手（或手指被系统收走）时收尾。`commit` 为假是组件卸载：只还原倍速，
   * 不替人锁定、不替人跳进度。
   */
  function finishBoost(commit: boolean) {
    const b = boost.current
    if (!b) return
    boost.current = null
    const video = videoRef.current
    if (b.step === 'seeking') {
      dragging.current = false
      if (commit && video) video.currentTime = b.seekTo
    }
    if (commit && b.step === 'lockArmed') {
      onLockRate()
      showNotice('locked')
    } else if (commit && b.step === 'unlockArmed') {
      onUnlockRate()
      showNotice('restored')
    } else if (video) {
      video.playbackRate = b.origin
    }
    setBoostStep(null)
    onBoostActiveChange(false)
  }

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const sync = () => {
      setPaused(video.paused)
      onPlayingChange(!video.paused)
      if (!dragging.current) setPosition(video.currentTime)
      setDuration(video.duration || 0)
    }
    const events = ['play', 'pause', 'timeupdate', 'loadedmetadata', 'durationchange'] as const
    events.forEach((e) => video.addEventListener(e, sync))
    sync()
    return () => {
      events.forEach((e) => video.removeEventListener(e, sync))
      onPlayingChange(false)
    }
    // onPlayingChange 是页面的 setState，引用不变
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef])

  // 组件卸载（比如换集）时手指还按着的话，别把顶速状态悬在上一屏身上。
  useEffect(() => () => {
    clearPressTimer()
    finishBoost(false)
    if (noticeTimer.current != null) window.clearTimeout(noticeTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function seekTo(clientX: number) {
    const video = videoRef.current
    const track = trackRef.current
    if (!video || !track || !video.duration) return
    const rect = track.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    const next = ratio * video.duration
    setPosition(next)
    video.currentTime = next
  }

  const percent = duration ? (position / duration) * 100 : 0
  const emphasized = paused || scrubbing || boostStep === 'seeking'
  const barHeight = emphasized ? 4 : 2

  // 视频底部的小胶囊，照红果实机：按住时深色带双箭头，拉够了、松手后的结论是浅色带锁（规格 2.4）。拖进度时不出
  const pill: { icon: ReactNode; text: string; light: boolean } | null =
    boostStep === 'hold'
      ? rateLocked
        ? { icon: <DoubleDownIcon size={22} />, text: t.rate.unlockHint, light: false }
        : { icon: <DoubleUpIcon size={22} />, text: t.rate.lockHint, light: false }
      : boostStep === 'lockArmed'
        ? { icon: <UnlockIcon size={22} />, text: t.rate.lockRelease, light: true }
        : boostStep === 'unlockArmed'
          ? { icon: <LockIcon size={22} />, text: t.rate.unlockRelease, light: true }
          : boostStep === null && notice
            ? {
                icon: notice === 'locked' ? <LockIcon size={22} /> : <UnlockIcon size={22} />,
                text: (notice === 'locked' ? t.rate.locked : t.rate.restored).replace('{n}', rateNumber(rate)),
                light: true,
              }
            : null
  // 按住期间只留视频、进度线、黑条和胶囊（红果实机）
  const boosting = boostStep !== null

  return (
    <div
      style={{ ...styles.layer, ...(hidden ? styles.hidden : null) }}
      // 长按是我们自己的手势，不能再让 WebView 弹系统菜单（存图/拷贝）
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        if (moved.current || longPressFired.current) return
        onTap()
      }}
      onTouchStart={(e) => {
        moved.current = false
        longPressFired.current = false
        clearPressTimer()
        const p = e.touches[0]
        if (!p) return
        const rect = e.currentTarget.getBoundingClientRect()
        pressStart.current = { x: p.clientX, y: p.clientY }
        lastPoint.current = { x: p.clientX, y: p.clientY }
        const zone = zoneFor(p.clientX, rect)
        pressTimer.current = window.setTimeout(() => {
          pressTimer.current = null
          longPressFired.current = true
          if (zone === 'middle') onOpenQuickMenu()
          else startBoost()
        }, LONG_PRESS_MS)
      }}
      onTouchMove={(e) => {
        moved.current = true
        const p = e.touches[0]
        if (!p) return
        lastPoint.current = { x: p.clientX, y: p.clientY }
        const dx = p.clientX - pressStart.current.x
        const dy = p.clientY - pressStart.current.y
        // 位移已经不小了，这是在滑/拖，不是在长按——取消候选，交还给原有判定
        if (pressTimer.current != null && (Math.abs(dx) >= DIRECTION_LOCK_PX || Math.abs(dy) >= DIRECTION_LOCK_PX)) {
          clearPressTimer()
        }
        moveBoost(p.clientX, p.clientY)
      }}
      onTouchEnd={() => {
        clearPressTimer()
        finishBoost(true)
      }}
      onTouchCancel={() => {
        clearPressTimer()
        finishBoost(true)
      }}
    >
      {paused && (
        <svg style={styles.centerIcon} width="64" height="64" viewBox="0 0 64 64" aria-hidden>
          <path
            d="M23 17.6c0-2 2.2-3.2 3.9-2.1l20.6 13.4c1.5 1 1.5 3.2 0 4.2L26.9 46.5c-1.7 1.1-3.9-.1-3.9-2.1Z"
            fill="rgba(255,255,255,.8)"
          />
        </svg>
      )}

      {clean || boosting ? (
        clean && (
        <div style={styles.cleanBar} onClick={(e) => e.stopPropagation()}>
          <button style={styles.cleanExit} onClick={onExitClean}>
            {t.cleanScreen.exit}
          </button>
        </div>
        )
      ) : (
        <>
          <div style={styles.topScrim} aria-hidden />
          <div style={styles.top} onClick={(e) => e.stopPropagation()}>
            <span style={styles.episodeTitle}>
              {t.episodeLabel} {episodeNumber}
            </span>
            {/* 照红果 VideoSpeedLayout：24 的仪表盘图标 + 4 + 14 粗的倍速；点开视频设置面板 */}
            <button style={styles.rateBtn} onClick={onOpenRate}>
              <SpeedIcon />
              <span style={{ color: rateActive ? 'var(--rate-active)' : '#fff' }}>{rateLabel}</span>
            </button>
            <button style={styles.moreBtn} onClick={onOpenQuickMenu} aria-label={t.rate.title}>
              <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
                <circle cx="12" cy="5" r="2" fill="#fff" />
                <circle cx="12" cy="12" r="2" fill="#fff" />
                <circle cx="12" cy="19" r="2" fill="#fff" />
              </svg>
            </button>
          </div>

          <div style={styles.bottomScrim} aria-hidden />
          <div style={styles.bottom} onClick={(e) => e.stopPropagation()}>
            {hint}
            {muted && (
              // 点屏幕任意处都能开声音（Player 的 onTap 管），这里只是把这件事说出来
              <button style={styles.unmute} onClick={onTap}>
                🔇 {t.unmute}
              </button>
            )}

            <span style={styles.title}>{title}</span>

            <div
              ref={trackRef}
              style={styles.track}
              role="slider"
              aria-label={t.episodeLabel}
              aria-valuemin={0}
              aria-valuemax={Math.round(duration)}
              aria-valuenow={Math.round(position)}
              tabIndex={0}
              // 进度条自己吃掉触摸，不让它冒到 EpisodeFeed：
              // 不拦的话，横向拖进度时只要手指上飘超过阈值就会跳到下一集。
              onTouchStart={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onPointerDown={(e) => {
                dragging.current = true
                setScrubbing(true)
                e.currentTarget.setPointerCapture(e.pointerId)
                seekTo(e.clientX)
              }}
              onPointerMove={(e) => dragging.current && seekTo(e.clientX)}
              onPointerUp={(e) => {
                dragging.current = false
                setScrubbing(false)
                e.currentTarget.releasePointerCapture(e.pointerId)
              }}
            >
              <div style={{ ...styles.rail, height: barHeight }} />
              <div
                style={{
                  ...styles.fill,
                  height: barHeight,
                  width: `${percent}%`,
                  background: emphasized ? 'var(--accent)' : 'rgba(255,255,255,.4)',
                }}
              />
              {emphasized && <div style={{ ...styles.knob, left: `${percent}%` }} />}
            </div>
          </div>
        </>
      )}

      {boosting && !clean && (
        // 进度线照留：顶栏、剧名那一摞藏了，进度条单独摆回视频底边
        <div style={styles.boostTrack} aria-hidden>
          <div style={{ ...styles.rail, height: barHeight }} />
          <div
            style={{
              ...styles.fill,
              height: barHeight,
              width: `${percent}%`,
              background: emphasized ? 'var(--accent)' : 'rgba(255,255,255,.4)',
            }}
          />
          {emphasized && <div style={{ ...styles.knob, left: `${percent}%` }} />}
        </div>
      )}

      {pill && (
        <div style={{ ...styles.pill, ...(pill.light ? styles.pillLight : null) }} aria-live="polite">
          {pill.icon}
          <span>{pill.text}</span>
        </div>
      )}
    </div>
  )
}

/**
 * 长按顶速时盖在选集条位置的黑条，照红果实机：不压视频，同高整条黑底；
 * 只有一行，快进动画 + 「2.0 倍速快进中」，数字着强调色。按住才有，松手就收（页面按 onBoostActiveChange 换上换下）。
 */
export function BoostBand() {
  const [before, after] = t.rate.boosting.split('{n}')
  return (
    <div style={styles.boostBand} aria-live="polite">
      <FastForwardChase />
      <span>
        {before}
        <span style={{ color: 'var(--rate-active)' }}>{rateNumber(BOOST_RATE)}</span>
        {after}
      </span>
    </div>
  )
}

const styles = {
  layer: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    transition: 'opacity .2s ease',
    // 同上：长按别选中剧名文字、别弹 iOS 的放大镜/callout
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
  },
  hidden: { opacity: 0, pointerEvents: 'none' },
  // 照红果：64 的圆角三角，80% 白，没有圆底，在视频区里居中
  centerIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    filter: 'drop-shadow(var(--video-shadow))',
    pointerEvents: 'none',
  },
  // 长按顶速的黑条：顶替选集条，同高（含底部安全区），一行居中
  boostBand: {
    flex: '0 0 auto',
    height: `calc(${BAND_HEIGHT}px + env(safe-area-inset-bottom))`,
    paddingBottom: 'env(safe-area-inset-bottom)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    background: '#000',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  // 顶速期间单独摆回去的进度线，位置和平时进度条一样贴着视频底边
  boostTrack: {
    position: 'absolute',
    left: 16,
    right: 16,
    bottom: 0,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  // 锁定引导 / 结论的胶囊：离视频底边 55、水平居中，高 40、全圆角，图标 22 + 8 + 14 的字
  pill: {
    position: 'absolute',
    left: '50%',
    bottom: 55,
    transform: 'translateX(-50%)',
    height: 40,
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderRadius: 20,
    background: 'rgba(0,0,0,.5)',
    color: '#fff',
    fontSize: 14,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
  pillLight: { background: 'rgba(255,255,255,.85)', color: '#333' },
  cleanBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 18,
    display: 'flex',
    justifyContent: 'center',
  },
  cleanExit: {
    fontSize: 14,
    padding: '6px 14px',
    borderRadius: 100,
    background: 'rgba(0,0,0,.4)',
    color: '#fff',
  },
  // 顶部和底部的压暗渐变垫在文字下面、视频上面；红果顶部只有 20%，纯白画面上字看不清，加深并补上底部
  topScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 'calc(108px + env(safe-area-inset-top))',
    background: 'linear-gradient(to bottom, var(--video-scrim-top), rgba(0,0,0,0))',
    pointerEvents: 'none',
  },
  bottomScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 200,
    background: 'linear-gradient(to top, var(--video-scrim-bottom), rgba(0,0,0,0))',
    pointerEvents: 'none',
  },
  // 照红果：栏高 44，「第 N 集」18 粗白字，倍速 14 粗，更多图标 24
  top: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 'calc(44px + env(safe-area-inset-top))',
    padding: 'env(safe-area-inset-top) 10px 0 16px',
    display: 'flex',
    alignItems: 'center',
  },
  episodeTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
    textShadow: 'var(--video-shadow)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rateBtn: {
    flex: '0 0 auto',
    height: 44,
    padding: '0 6px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
    filter: 'drop-shadow(var(--video-shadow))',
    background: 'transparent',
  },
  moreBtn: {
    flex: '0 0 auto',
    width: 40,
    height: 44,
    marginLeft: 8,
    display: 'grid',
    placeItems: 'center',
    background: 'transparent',
    filter: 'drop-shadow(var(--video-shadow))',
  },
  bottom: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  unmute: {
    alignSelf: 'center',
    fontSize: 13,
    padding: '8px 16px',
    borderRadius: 100,
    background: 'rgba(255,255,255,.26)',
  },
  title: {
    padding: '0 16px',
    fontSize: 16,
    fontWeight: 700,
    color: '#fff',
    textShadow: 'var(--video-shadow)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // 照红果的两态进度条：播放中 2、40% 白；暂停或拖动时 4、主色，带 6 的白点。触控区 16 高，左右内缩 16
  track: {
    position: 'relative',
    height: 16,
    margin: '0 16px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    touchAction: 'none',
  },
  rail: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderRadius: 2,
    background: 'rgba(255,255,255,.1)',
    transition: 'height .15s cubic-bezier(.42,0,.58,1)',
  },
  fill: {
    position: 'absolute',
    left: 0,
    borderRadius: 2,
    transition: 'height .15s cubic-bezier(.42,0,.58,1), background-color .15s cubic-bezier(.42,0,.58,1)',
  },
  knob: {
    position: 'absolute',
    width: 6,
    height: 6,
    marginLeft: -3,
    borderRadius: '50%',
    background: '#fff',
  },
} satisfies Record<string, React.CSSProperties>
